<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card :style="{ minWidth: '600px' }" class="relative-position">
      <q-card-section
        class="q-px-2x-md q-py-xl flex justify-between items-center"
      >
        <span class="text-lg text-neutral text-weight-medium">
          Apply Credits
        </span>
        <PBtn color="primary" flat dense round @click="onDialogCancel">
          <CloseIcon class="icon" />
        </PBtn>
      </q-card-section>
      <q-separator class="fullWidth" />
      <q-card-section class="q-px-2x-md q-pt-xl q-pb-2x-md">
        <q-form ref="pForm" greedy @submit.prevent="onSubmit">
          <PTable
            v-if="formData.applied_credits_lineitems.length"
            ref="pTable"
            :columns="columns"
            :rows="formData.applied_credits_lineitems"
            rows-per-page-label=""
            :rows-per-page-options="[]"
            :hide-pagination="true"
          >
            <template #body-cell-payment="props">
              <q-td :props="props">
                <PSelectInput
                  v-model="
                    formData.applied_credits_lineitems[props.rowIndex].number
                  "
                  :options="unref(paymentsMadeFilter.entities)"
                  :emit-value="false"
                  :map-options="false"
                  label=""
                  option-label="number"
                  option-value="number"
                  placeholder="Select payment#"
                  required
                  :disable="isEditModeDisabled"
                  @focus="() => onPaymentSelectFocus(props.rowIndex)"
                  @filter="paymentsMadeFilter.onFilter"
                  @filter-abort="paymentsMadeFilter.onFilterAbort"
                  @update:model-value="
                    (value) => onUpdateLineItem(value, props.rowIndex)
                  "
                >
                  <template #option="{ itemProps, opt }">
                    <q-item v-bind="itemProps">
                      <q-item-section>
                        <q-item-label class="text-neutral text-sm">
                          {{ opt?.number }}
                        </q-item-label>
                        <q-item-label caption lines="1">
                          {{ formatDate(new Date(opt.date), "DD MMM YYYY") }} |
                          {{
                            numberFormat({
                              number: opt?.total_amount || 0,
                              style: "currency",
                            })
                          }}
                        </q-item-label>
                        <q-item-label
                          v-if="opt.internal_number"
                          caption
                          lines="1"
                        >
                          ref PR:
                          {{ " " + opt?.internal_number }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </template>
                </PSelectInput>
              </q-td>
            </template>
            <template #body-cell-available_amount="props">
              <q-td :props="props">
                <p class="text-neutral q-mb-none">
                  {{
                    numberFormat({
                      number: props.row.available_amount ?? 0,
                      style: "currency",
                      mfd: 2,
                    })
                  }}
                </p>

                <PBtn
                  v-if="!isEditModeDisabled"
                  label="Apply All"
                  class="q-mt-none"
                  size="sm"
                  :disable="!props.row.available_amount"
                  dense
                  flat
                  fluid
                  text
                  @click="() => applyAll(props)"
                />
              </q-td>
            </template>
            <template #body-cell-applied_amount="props">
              <q-td :props="props">
                <PInput
                  v-model="props.row.applied_amount"
                  :disable="!props.row.number || isEditModeDisabled"
                  input-class="ellipsis "
                  class="prepend-bg"
                  type="number"
                  placeholder="0.00"
                  :validations="{
                    label: 'Amount',
                    required: true,
                    type: 'float',
                    gte: 1,
                    lte: floorOff(props.row.available_amount, 2),
                    style: 'currency',
                  }"
                  @update:model-value="
                    (value) => updateAppliedCredits(value, props.rowIndex)
                  "
                >
                  <template #prepend>
                    <span class="text-sm">{{ "₹" }}</span>
                  </template>
                </PInput>
              </q-td>
            </template>
            <template #body-cell-action="props">
              <q-td :props="props">
                <PBtn
                  dense
                  flat
                  rounded
                  @click="() => handleRemoveLineItem(props.rowIndex)"
                >
                  <RemoveIcon class="icon--md" />
                  <PTooltip> Remove Item</PTooltip>
                </PBtn>
              </q-td>
            </template>
          </PTable>
          <PBtn
            v-if="!isEditModeDisabled"
            class="q-mt-md"
            outline
            dense
            flat
            @click="handleAddLineItem"
          >
            <AddIcon class="icon--sm q-mr-sm" />
            <span>Add More</span>
          </PBtn>
          <div class="flex justify-end">
            <div>
              <PInput
                v-model.number="totalAppliedCredits"
                :disable="true"
                label="Applied Amount"
                label-position="side"
                :placeholder-width="true"
                class="prepend-bg col"
                placeholder="0.00"
                type="number"
              >
                <template #prepend>
                  <span class="text-sm">₹</span>
                </template>
              </PInput>
            </div>
          </div>
          <div class="flex justify-end q-mt-md">
            <div>
              <PInput
                v-model.number="formData.adjusted_credits"
                :disable="isEditModeDisabled"
                :validations="{
                  type: 'integer',
                  gte: 0,
                }"
                :step="1"
                :min="0"
                label="Adjustments"
                label-position="side"
                required
                :placeholder-width="true"
                class="prepend-bg col"
                placeholder="0.00"
                type="number"
              >
                <template #prepend>
                  <span class="text-sm">₹</span>
                </template>
              </PInput>
            </div>
          </div>

          <div v-if="!isEditModeDisabled" class="flex justify-end">
            <PBtn
              class="q-mt-md"
              :loading="loading"
              :fluid="false"
              type="submit"
            >
              <span>Apply </span>
            </PBtn>
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { date, useDialogPluginComponent } from "quasar";
import {
  numberFormat,
  floorOff,
  roundOff,
} from "assets/scripts/utilities/helpers";

import AddIcon from "assets/icons/add.svg";
import RemoveIcon from "assets/icons/remove.svg";
import CloseIcon from "assets/icons/close.svg";

import { storeToRefs } from "pinia";
import { usePaymentRequestStore } from "src/stores/payment-request";
import { notify } from "src/boot/api";

const { formatDate } = date;

defineEmits(["cancel", "ok", "hide"]);

const appliedCreditsLineItem = {
  available_amount: null,
  number: null,
  internal_number: null,
  applied_amount: null,
  total_amount: null,
  payment_made_id: null,
};

const { paymentRequest } = storeToRefs(usePaymentRequestStore());

const loading = ref(false);

const { getUserPortfolio } = useUserStore();

const passedProps = defineProps({
  lineitems: {
    type: Array,
    default: () => [],
  },
  adjustedCredits: {
    type: Number,
    default: 0,
  },
  netAmount: {
    type: Number,
    default: 0,
  },
});

const isEditModeDisabled = computed(() => {
  return unref(paymentRequest)?.status_cd !== "pending";
});

const pForm = ref(null);

const formData = ref({
  applied_credits_lineitems: [{ ...appliedCreditsLineItem }],
  adjusted_credits: 0,
});

const updateAppliedCredits = (applied_credits, index) => {
  unref(formData).applied_credits_lineitems[index].applied_amount = floorOff(
    applied_credits,
    2,
  );
};

onBeforeMount(() => {
  formData.value.adjusted_credits = passedProps.adjustedCredits || 0;

  if (!passedProps.lineitems?.length) {
    return;
  }

  formData.value.applied_credits_lineitems = passedProps.lineitems.map(
    (item) => {
      if (unref(paymentRequest)?.status_cd === "pending") {
        return item;
      }

      return {
        ...item,
        available_amount: roundOff(
          item.available_amount + item.applied_amount,
          2,
        ),
      };
    },
  );
});

const totalAppliedCredits = computed(() => {
  return unref(formData).applied_credits_lineitems?.reduce(
    (sum, payment) => sum + +payment.applied_amount,
    0,
  );
});

const paymentsMadeFilter = ref({});

const onPaymentSelectFocus = (rowIndex) => {
  const number = unref(formData)
    .applied_credits_lineitems.map((item) => item.number)
    .filter((num, idx) => idx !== rowIndex)
    .join(",");

  paymentsMadeFilter.value = useFilter({
    entity: "paymentMade",
    options: {
      params: {
        seller_id: unref(paymentRequest)?.seller?._id,
        _reference_pr_id: unref(paymentRequest)?.internal_number,
        is_advance: 1,
        _number: number,
        source: "payment-requests",
      },
    },
  });
};

const columns = computed(() => [
  {
    align: "left",
    name: "payment",
    label: "Payment#",
    field: "number",
  },
  {
    align: "right",
    name: "available_amount",
    label: "Unused Credits",
    field: "available_amount",
  },
  {
    align: "right",
    name: "applied_amount",
    label: "Apply Amount",
    field: "applied_amount",
  },
  {
    hidden: unref(isEditModeDisabled),
    align: "left",
    classes: "sticky--right",
    headerClasses: "sticky--right",
    name: "action",
    label: "",
  },
]);

const { dialogRef, onDialogHide, onDialogCancel, onDialogOK } =
  useDialogPluginComponent();

const onSubmit = async () => {
  try {
    const success = await pForm.value.validate();

    if (!success) return;

    if (unref(totalAppliedCredits) > passedProps.netAmount) {
      notify({
        message:
          "Applied credits exceed the net payable. Please adjust the applied credits.",
        type: "negative",
      });

      return;
    }

    if (unref(formData).adjusted_credits > passedProps.netAmount) {
      notify({
        message:
          "Adjusted Credits exceed the net payable. Please adjust the adjusted credits.",
        type: "negative",
      });

      return;
    }

    if (
      unref(totalAppliedCredits) + unref(formData).adjusted_credits >
      passedProps.netAmount
    ) {
      notify({
        message:
          "Applied credits and Adjusted Credits exceed the net payable. Please adjust the applied credits.",
        type: "negative",
      });

      return;
    }

    loading.value = true;

    const seller_id =
      unref(paymentRequest)?.seller?._id || unref(paymentRequest)?.seller;

    const userPortfolio = await getUserPortfolio(seller_id);

    const unused_credits_payable_amount =
      userPortfolio.unused_credits_payable_amount;

    if (+unref(totalAppliedCredits) > +unused_credits_payable_amount) {
      notify({
        message: `Total Applied Credits (${numberFormat({
          number: unref(totalAppliedCredits),
          style: "currency",
        })}) cannot be greater than Total Unused credits (${numberFormat({
          number: unref(unused_credits_payable_amount),
          style: "currency",
        })}) for this Vendor. Refresh the page and try again later `,
        type: "negative",
      });

      return;
    }

    loading.value = false;

    const payload = {
      applied_credits_lineitems: unref(formData).applied_credits_lineitems,
      totalAppliedCredits: unref(totalAppliedCredits),
      adjusted_credits: unref(formData).adjusted_credits,
    };

    // close the dialog
    onDialogOK(payload);
  } catch (error) {
    console.error(error);

    loading.value = false;
  }
};

const handleRemoveLineItem = (index) => {
  unref(formData).applied_credits_lineitems.splice(index, 1);
};

const handleAddLineItem = () => {
  unref(formData).applied_credits_lineitems.push({ ...appliedCreditsLineItem });
};

const onUpdateLineItem = (payment, index) => {
  unref(formData).applied_credits_lineitems[index] = payment;
};

const getMaxValue = (props) => {
  const { row } = props;

  const { applied_credits_lineitems } = unref(formData);

  const totalAppliedCredits = applied_credits_lineitems?.reduce(
    (acc, payable) => {
      if (payable._id === row._id) {
        return acc;
      }

      return acc + Number(payable.applied_amount);
    },
    0,
  );

  const currentRowAvailableAmount = Math.max(row.available_amount, 0);

  const currentTotalAvailableAmount = Math.max(
    passedProps.netAmount - totalAppliedCredits,
    0,
  );

  return floorOff(
    Math.min(
      currentRowAvailableAmount,
      passedProps.netAmount,
      currentTotalAvailableAmount,
    ),
    2,
  );
};

const applyAll = (props) => {
  unref(formData).applied_credits_lineitems[props.rowIndex].applied_amount =
    getMaxValue(props);
};
</script>
